// src/pages/auth/ForgotPassword.js
import React, {useState} from 'react';
import {StyleSheet, css} from 'aphrodite';
import {FiLock} from 'react-icons/fi';
import {useNavigate} from 'react-router-dom';
import {requestPasswordReset} from '../../api/api';

const logo = '/logo192.png';

const ForgotPassword = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleReset = async e => {
    e.preventDefault();

    if (!email.trim()) {
      setMessage('Please enter your email.');
      return;
    }

    try {
      setLoading(true);
      setMessage('');

      await requestPasswordReset({email});

      setMessage('OTP sent to your email.');
      navigate('/otp', {state: {email}});
    } catch (err) {
      setMessage(
        err.response?.data?.message || err.message || 'Failed to send OTP.',
      );
    } finally {
      setLoading(false);
    }
  };

  const isErrorMessage =
    message &&
    !message.toLowerCase().includes('sent') &&
    !message.toLowerCase().includes('success');

  return (
    <div className={css(styles.page)}>
      <div className={css(styles.shell)}>
        <section className={css(styles.heroCard)}>
          <div className={css(styles.logoBadge)}>
            <img
              src={logo}
              alt="YaBatho Logo"
              className={css(styles.logoImage)}
            />
          </div>

          <p className={css(styles.kicker)}>Account Recovery</p>

          <h1 className={css(styles.heroTitle)}>Reset your password</h1>

          <p className={css(styles.heroText)}>
            Enter your YaBatho customer account email. We will send you a
            verification code so you can create a new password securely.
          </p>

          <div className={css(styles.noticeCard)}>
            <span className={css(styles.noticeIcon)} aria-hidden="true"><FiLock /></span>

            <div>
              <strong className={css(styles.noticeTitle)}>Secure reset</strong>
              <p className={css(styles.noticeText)}>
                Your OTP will be used to verify that this account belongs to
                you.
              </p>
            </div>
          </div>
        </section>

        <section className={css(styles.card)}>
          <div className={css(styles.formHeader)}>
            <p className={css(styles.formKicker)}>Forgot Password</p>
            <h2 className={css(styles.title)}>Send OTP</h2>
            <p className={css(styles.subtitle)}>
              Enter your email address below.
            </p>
          </div>

          <form onSubmit={handleReset} className={css(styles.form)}>
            <label className={css(styles.label)}>Email Address</label>

            <input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className={css(styles.input)}
            />

            {message ? (
              <div
                className={css(
                  styles.messageBox,
                  isErrorMessage ? styles.errorBox : styles.successBox,
                )}>
                <span
                  className={css(
                    styles.messageIcon,
                    isErrorMessage ? styles.errorIcon : styles.successIcon,
                  )}>
                  {isErrorMessage ? '!' : '✓'}
                </span>

                <p
                  className={css(
                    styles.message,
                    isErrorMessage ? styles.errorText : styles.successText,
                  )}>
                  {message}
                </p>
              </div>
            ) : null}

            <button
              type="submit"
              className={css(styles.button, loading && styles.buttonDisabled)}
              disabled={loading}>
              {loading ? 'Sending...' : 'Send OTP'}
            </button>
          </form>

          <button
            type="button"
            onClick={() => navigate('/')}
            className={css(styles.linkButton)}>
            Back to Login
          </button>
        </section>
      </div>
    </div>
  );
};

const styles = StyleSheet.create({
  page: {
    minHeight: '100vh',
    width: '100%',
    backgroundColor: '#FFFDF7',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '24px',
    boxSizing: 'border-box',
    fontFamily: 'Inter, Segoe UI, Arial, sans-serif',
  },

  shell: {
    width: '100%',
    maxWidth: '1050px',
    display: 'grid',
    gridTemplateColumns: '1fr 0.9fr',
    gap: '22px',
    alignItems: 'stretch',

    '@media (max-width: 820px)': {
      gridTemplateColumns: '1fr',
    },
  },

  heroCard: {
    backgroundColor: '#0F3D2E',
    borderRadius: '32px',
    padding: '34px',
    color: '#ffffff',
    boxShadow: '0 22px 50px rgba(15, 61, 46, 0.18)',
    border: '1px solid rgba(201, 161, 74, 0.45)',

    '@media (max-width: 520px)': {
      padding: '24px',
      borderRadius: '28px',
    },
  },

  logoBadge: {
    width: '78px',
    height: '78px',
    borderRadius: '50%',
    border: '2px solid #C9A14A',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: '22px',
    boxShadow: '0 14px 30px rgba(0, 0, 0, 0.14)',
    overflow: 'hidden',

    '@media (max-width: 520px)': {
      width: '68px',
      height: '68px',
    },
  },

  logoImage: {
    width: '62px',
    height: '62px',
    objectFit: 'contain',
    display: 'block',

    '@media (max-width: 520px)': {
      width: '54px',
      height: '54px',
    },
  },

  kicker: {
    margin: '0 0 8px',
    color: '#C9A14A',
    fontSize: '12px',
    fontWeight: '900',
    letterSpacing: '1px',
    textTransform: 'uppercase',
  },

  heroTitle: {
    margin: 0,
    fontSize: '42px',
    lineHeight: 1.06,
    letterSpacing: '-1.8px',
    fontWeight: '900',
    maxWidth: '520px',

    '@media (max-width: 520px)': {
      fontSize: '32px',
    },
  },

  heroText: {
    margin: '18px 0 28px',
    color: '#F4EBD8',
    fontSize: '15px',
    lineHeight: 1.8,
    maxWidth: '540px',
  },

  noticeCard: {
    backgroundColor: 'rgba(255, 253, 247, 0.08)',
    border: '1px solid rgba(230, 216, 190, 0.22)',
    borderRadius: '22px',
    padding: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },

  noticeIcon: {
    width: '42px',
    height: '42px',
    borderRadius: '50%',
    backgroundColor: '#C9A14A',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },

  noticeTitle: {
    display: 'block',
    color: '#ffffff',
    fontSize: '14px',
    marginBottom: '3px',
  },

  noticeText: {
    margin: 0,
    color: '#F4EBD8',
    fontSize: '12px',
    lineHeight: 1.4,
  },

  card: {
    backgroundColor: '#ffffff',
    padding: '28px',
    borderRadius: '32px',
    boxShadow: '0 12px 34px rgba(30, 30, 30, 0.06)',
    width: '100%',
    boxSizing: 'border-box',
    border: '1px solid #E6D8BE',

    '@media (max-width: 520px)': {
      padding: '22px',
      borderRadius: '28px',
    },
  },

  formHeader: {
    marginBottom: '22px',
  },

  formKicker: {
    margin: '0 0 6px',
    color: '#C9A14A',
    fontSize: '11px',
    fontWeight: '900',
    letterSpacing: '0.9px',
    textTransform: 'uppercase',
  },

  title: {
    margin: 0,
    fontSize: '30px',
    color: '#0F3D2E',
    fontWeight: '900',
    letterSpacing: '-0.8px',
  },

  subtitle: {
    margin: '8px 0 0',
    color: '#8C8170',
    fontSize: '14px',
    lineHeight: 1.6,
  },

  form: {
    display: 'flex',
    flexDirection: 'column',
  },

  label: {
    color: '#0F3D2E',
    fontSize: '13px',
    fontWeight: '800',
    marginBottom: '7px',
  },

  input: {
    width: '100%',
    padding: '14px 15px',
    fontSize: '15px',
    borderRadius: '18px',
    border: '1px solid #E6D8BE',
    backgroundColor: '#FFFDF7',
    color: '#0F3D2E',
    outline: 'none',
    marginBottom: '14px',
    boxSizing: 'border-box',

    ':focus': {
      borderColor: '#C9A14A',
      boxShadow: '0 0 0 3px rgba(201, 161, 74, 0.15)',
    },
  },

  button: {
    padding: '15px',
    fontSize: '15px',
    backgroundColor: '#C9A14A',
    color: '#0F3D2E',
    border: 'none',
    borderRadius: '20px',
    fontWeight: '900',
    marginTop: '8px',
    boxShadow: '0 10px 24px rgba(201, 161, 74, 0.2)',
    cursor: 'pointer',

    ':hover': {
      transform: 'translateY(-1px)',
    },
  },

  buttonDisabled: {
    opacity: 0.7,
    cursor: 'not-allowed',
  },

  messageBox: {
    borderRadius: '18px',
    padding: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '9px',
    marginBottom: '14px',
  },

  errorBox: {
    backgroundColor: '#FFE9E9',
    border: '1px solid #F5B8B8',
  },

  successBox: {
    backgroundColor: '#E9F7EF',
    border: '1px solid #BFE7CE',
  },

  messageIcon: {
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    color: '#ffffff',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '13px',
    fontWeight: '900',
    flexShrink: 0,
  },

  errorIcon: {
    backgroundColor: '#D9534F',
  },

  successIcon: {
    backgroundColor: '#27AE60',
  },

  message: {
    fontSize: '13px',
    margin: 0,
    fontWeight: '700',
    lineHeight: 1.5,
  },

  errorText: {
    color: '#D9534F',
  },

  successText: {
    color: '#0F3D2E',
  },

  linkButton: {
    width: '100%',
    marginTop: '18px',
    backgroundColor: 'transparent',
    border: 'none',
    color: '#0F3D2E',
    fontWeight: '900',
    cursor: 'pointer',
    fontSize: '14px',
    padding: 0,
    textAlign: 'center',

    ':hover': {
      color: '#B85C38',
    },
  },
});

export default ForgotPassword;
